import { useEffect, useState } from 'react';
import { useApp } from '../../../contexts/AppContext';
import { sendInquiry } from '../../../api';
import styled from 'styled-components';
import config from '../../../config';
import {
  PageMainTitle,
  SubInner,
  SectionTitle,
  InputLabel,
  FormTextArea,
  RequiredLabel,
  FormInput,
} from '../../atoms/CommonParts';
import DBDPullDown from '../../atoms/DBDPullDown';
import {
  useCustomDialog,
  ButtonLayout,
} from '../../../contexts/CustomDialogContext';
import { Link, useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useLang, withLang } from '../../../i18n/langPath';

const Page = styled.div`
  padding-top: 47px;
  @media ${config.layout.wide} {
    padding-top: 69px;
  }
`;

const PageFeatureTitle = styled.div`
  position: relative;

  h2 {
    font-size: 50px;
    font-weight: 300;

    span {
      display: block;
      font-size: 16px;
      font-weight: 400;
      line-height: 26px;
      margin-top: 11px;
    }

    @media ${config.layout.wide} {
      font-size: 70px;
      span {
        display: inline-block;
        margin-top: 0;
        margin-left: 37px;
      }
    }
  }
`;

const Inner = styled.div`
  margin-top: 47px;
  @media ${config.layout.wide} {
    margin-top: 84px;
    padding: 0 64px;
  }
`;

const TopLabel = styled.div`
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  a {
    color: unset;
    text-decoration: underline;
  }
  div {
    margin-bottom: 20px;
    &:last-child {
      margin-bottom: 50px;
      @media ${config.layout.wide} {
        margin-bottom: 100px;
      }
    }
  }
`;

const ThanksTitle = styled.h3`
  font-size: 40px;
  font-weight: bold;
  text-align: left;
  margin-top: 91px;
  margin-bottom: 40px;
  @media ${config.layout.wide} {
    margin-top: 85px;
  }
`;

const ThanksDescription = styled.div`
  font-size: 16px;
  line-height: 26px;
  font-weight: 400;
  padding-bottom: 50px;
  @media ${config.layout.wide} {
    margin-bottom: 100px;
  }
`;

const SubmitButtonWrap = styled.div`
  @media ${config.layout.wide} {
    width: 350px;
    margin: 0 auto;
  }
`;

const KeepMeLogin = styled.div`
  text-align: center;
  margin: 50px auto;
  max-width: calc(100% - 30px);
  @media ${config.layout.wide} {
    max-width: 100%;
  }

  input {
    display: none;

    &:checked {
      & + label {
        &:before {
        }
      }
    }
  }

  label {
    position: relative;
    font-size: 12px;
    @media ${config.layout.wide} {
      font-size: 14px;
    }
    font-weight: 400;
    margin-left: 7px;
    padding-left: 28px;
    text-align: left;

    &:before {
      position: absolute;
      left: 0;
      top: 0px;
      @media ${config.layout.wide} {
        top: 2px;
      }
    }

    a {
      color: #000;
    }
  }
`;

const FormDescription = styled.div`
  font-size: 16px;
  line-height: 21px;
  font-weight: 400;
  margin-top: 20px;
`;

function ContactSent() {
  const { t } = useTranslation();
  const lang = useLang();
  return (
    <Page>
      <PageFeatureTitle>
        <h2>{t('contact.titleHeading')}</h2>
      </PageFeatureTitle>

      <ThanksTitle>{t('contact.thanksTitle')}</ThanksTitle>
      <ThanksDescription>{t('contact.thanksDescription')}</ThanksDescription>
      <SubmitButtonWrap>
        <Link
          className="dark-button back-button center-fixed"
          to={withLang(lang, '/')}
          style={{
            width: '100%',
          }}
        >
          <span>{t('common.top')}</span>
        </Link>
      </SubmitButtonWrap>
    </Page>
  );
}

function ContactUnauth({ setSent }) {
  const { t } = useTranslation();
  const { state } = useLocation();
  const [category, setCategory] = useState(
    (state && state.category) || 'default',
  );
  const { customDialog, errorDialogWithAPIError, alertDialog } =
    useCustomDialog();
  const [agreed, setAgreed] = useState(false);
  const lang = useLang();

  useEffect(() => {
    try {
      let st = window.history.state;
      if (st && st.usr && st.usr.category) {
        delete st.usr.category;
        window.history.replaceState(st, document.title);
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  // `value` stays the stable English identifier the backend expects;
  // only `label` (the user-facing text) is localized.
  const categories = [
    { value: 'Press', label: t('contact.categories.press') },
    {
      value: 'KUVO account (created before Sep 2022)',
      label: t('contact.categories.kuvoAccount'),
    },
    {
      value: 'New venue account',
      label: t('contact.categories.newVenueAccount'),
    },
    { value: 'CMO account', label: t('contact.categories.cmoAccount') },
    { value: 'Others', label: t('contact.categories.others') },
  ];

  function handleSubmit(event) {
    event.preventDefault();

    if (!agreed) {
      return;
    }

    let formData = new FormData(event.currentTarget);
    let name = (formData.get('name') || '').trim().substr(0, 256);
    let email = (formData.get('email') || '').trim().substr(0, 256);
    let description = (formData.get('description') || '').trim();
    let errors = [];
    if (!name) {
      errors.push(t('contact.errors.nameRequired'));
    }
    if (!email) {
      errors.push(t('contact.errors.emailRequired'));
    }
    if (category == 'default') {
      errors.push(t('contact.errors.chooseSubject'));
    }
    if (!description) {
      errors.push(t('contact.errors.commentRequired'));
    }

    if (errors.length) {
      (async function () {
        await alertDialog({
          //buttons: ButtonLayout.None,
          description_element: (
            <div className={'errors'}>
              <ul>
                {errors.map((e, i) => {
                  return <li key={i}>{e}</li>;
                })}
              </ul>
            </div>
          ),
        });
      })();
      return;
    }

    (async () => {
      try {
        await sendInquiry({
          name,
          email,
          category,
          description,
        });
        setSent(true);
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 100);
      } catch (e) {
        console.error(e);
        await errorDialogWithAPIError(e);
      }
    })();
  }

  const onCategoryPullDownChange = (newValue) => {
    console.log(newValue);
    setCategory(newValue);
  };

  return (
    <Page>
      <PageFeatureTitle>
        <h2>{t('contact.titleHeading')}</h2>
      </PageFeatureTitle>

      <Inner>
        <TopLabel>
          <div>
            <Trans
              i18nKey="contact.faqIntro"
              components={[<Link key="0" to="/faq" />]}
            />
          </div>
          <div>
            <Trans
              i18nKey="contact.supportIntro"
              components={[
                <a
                  key="0"
                  href="https://forums.pioneerdj.com/hc/requests/new?ticket_form_id=175743"
                  target="_blank"
                  rel="noopener"
                />,
              ]}
            />
          </div>
          <div>
            <Trans
              i18nKey="contact.rekordboxIntro"
              components={[
                <a
                  key="0"
                  href={`https://rekordbox.com/${lang || 'en'}/support/contact/`}
                  target="_blank"
                  rel="noopener"
                />,
              ]}
            />
          </div>
        </TopLabel>
        <form onSubmit={handleSubmit}>
          <RequiredLabel />

          <FormDescription>{t('contact.titleSubheading')}</FormDescription>

          <InputLabel required={true}>{t('common.name')}</InputLabel>
          <FormInput
            type="text"
            required
            name="name"
            maxLength={64}
            onInvalid={(e) => {
              if (window.scrollY > 140) {
                window.scrollTo(0, 140);
              }
            }}
          />
          <InputLabel required={true}>{t('common.emailAddress')}</InputLabel>
          <FormInput
            type="email"
            required
            name="email"
            maxLength={128}
            onInvalid={(e) => {
              if (window.scrollY > 140) {
                window.scrollTo(0, 140);
              }
            }}
          />
          <InputLabel required={true}>{t('contact.subject')}</InputLabel>
          <DBDPullDown
            defaultLabel={t('common.selectOne')}
            value={category}
            onChange={onCategoryPullDownChange}
            items={categories}
            style={{
              minWidth: '100%',
            }}
          ></DBDPullDown>
          <InputLabel required={true}>
            {t('contact.questionsComments')}
          </InputLabel>
          <FormTextArea required={true} name="description" maxLength={1000} />

          <KeepMeLogin>
            <input
              id="keep_me_logged_in"
              name="keep"
              type="checkbox"
              defaultChecked={agreed}
              onChange={(e) => {
                setAgreed(e.target.checked);
              }}
            />
            <label htmlFor="keep_me_logged_in">
              <Trans
                i18nKey="common.privacyPolicyAgreement"
                components={[
                  <a
                    key="0"
                    href={`https://alphatheta.com/${lang || 'en'}/company/privacy-policy/`}
                    target="_blank"
                    rel="noopener"
                  />,
                ]}
              />
            </label>
          </KeepMeLogin>

          <SubmitButtonWrap>
            <button
              type="submit"
              className="dark-button center-fixed"
              style={{
                width: '100%',
              }}
              disabled={!agreed}
            >
              <span>{t('common.submit')}</span>
            </button>
          </SubmitButtonWrap>
        </form>
      </Inner>
    </Page>
  );
}

export default function ContactPage() {
  const app = useApp();
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);

  useEffect(() => {
    app.setTitle(t('contact.documentTitle'));
  }, []);

  if (sent) {
    return <ContactSent />;
  } else {
    return <ContactUnauth setSent={setSent} />;
  }
}
